import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './ChapterLearn.module.css';
import api, { withApiPrefix } from '../../services/api';
import { LoaderIcon, SettingsIcon, HeartIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import WordModal from './components/WordModal/WordModal';
import WordsSection from './components/WordsSection/WordsSection';
import SentenceCard from './components/SentenceCard/SentenceCard';
import NoteGroupCard from './components/NoteCard/NoteCard';
import useAudioClipPlayer from '../../hooks/useAudioClipPlayer';
import { parseSubtitleContent, buildSentenceTimingMap } from '../../utils/subtitle';

const OSS_BASE_URL = 'https://xiaoshuoassets.oss-ap-southeast-1.aliyuncs.com/audios';

const Section = ({ title, emptyText, loading, children }) => (
  <section className={styles.section}>
    <header className={styles.sectionHeader}>
      {/* <h2>{title}</h2> */}
    </header>
    {loading ? (
      <div className={styles.loading}>
        <LoadingSpinner size="small" text="加载中..." />
      </div>
    ) : children && React.Children.count(children) > 0 ? (
    <div className={styles.sectionContent}>{children}</div>
    ) : (
      <div className={styles.empty}>{emptyText}</div>
    )}
  </section>
);

const ChapterLearn = () => {
  const [searchParams] = useSearchParams();
  const chapterId = searchParams.get('chapterid');
  const tabParam = searchParams.get('tab');
  const bookIdFromUrl = searchParams.get('bookid');
  const location = useLocation();
  const navigate = useNavigate();
  // console.log('[ChapterLearn] render, chapterId:', chapterId, 'bookIdFromUrl:', bookIdFromUrl, 'tab:', tabParam);
  const [bookId, setBookId] = useState(bookIdFromUrl);
  const [words, setWords] = useState([]);
  const [sentences, setSentences] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chapter, setChapter] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAllWordDefinitions, setShowAllWordDefinitions] = useState(false);
  const [showAllWordSentences, setShowAllWordSentences] = useState(false);
  const [expandedSentences, setExpandedSentences] = useState({});
  const [favoriteLoadingMap, setFavoriteLoadingMap] = useState({});
  const [showSentenceChinese, setShowSentenceChinese] = useState(true);
  const [showNoteChinese, setShowNoteChinese] = useState(true);
  const [showNoteEnglish, setShowNoteEnglish] = useState(true);
  const [sentenceFavoriteLoading, setSentenceFavoriteLoading] = useState({});
  const [noteFavoriteLoading, setNoteFavoriteLoading] = useState({});
  const [wordModalOpen, setWordModalOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);
  const [wordModalDictResults, setWordModalDictResults] = useState({ youdao: null, collins: null });
  const [wordModalLoading, setWordModalLoading] = useState({ youdao: false, collins: false });
  const [wordModalActiveDict, setWordModalActiveDict] = useState('youdao');
  const { playingClip, playSentenceClip, stopClip } = useAudioClipPlayer();
  const [sentenceTimings, setSentenceTimings] = useState({ en: {} });

  // 加载英文 SRT 时间戳
  useEffect(() => {
    let cancelled = false;
    const loadTimings = async () => {
      if (!chapterId) {
        setSentenceTimings({ en: {} });
        return;
      }
      const srtUrl = `${OSS_BASE_URL}/${chapterId}/en.srt`;
      try {
        const response = await fetch(srtUrl, { cache: 'no-cache' });
        if (!response.ok) throw new Error(`${response.status}`);
        const text = await response.text();
        if (cancelled) return;
        const segments = parseSubtitleContent(text);
        const enMap = buildSentenceTimingMap(segments);
        setSentenceTimings({ en: enMap });
      } catch (err) {
        if (!cancelled) setSentenceTimings({ en: {} });
      }
    };
    loadTimings();
    return () => { cancelled = true; };
  }, [chapterId]);

  // URL 中 bookId 变化时同步
  useEffect(() => {
    if (bookIdFromUrl && bookIdFromUrl !== bookId) {
      setBookId(bookIdFromUrl);
    }
  }, [bookIdFromUrl]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const [wordRes, sentenceRes, noteRes, chapterRes] = await Promise.all([
          api.get(`/chapters/${chapterId}/favorite-words`),
          api.get(`/chapters/${chapterId}/favorite-sentences`),
          api.get(`/chapters/${chapterId}/notes`),
          api.get(`/chapters/${chapterId}`)
        ]);

        setWords(Array.isArray(wordRes.data) ? wordRes.data : []);
        setSentences(Array.isArray(sentenceRes.data) ? sentenceRes.data : []);
        setNotes(Array.isArray(noteRes.data) ? noteRes.data : []);
        setChapter(chapterRes.data || null);
        // fallback: 如果 URL 没有 bookId，从 API 返回中获取
        if (!bookIdFromUrl) {
          const resolvedBookId = chapterRes.data?.book_id || chapterRes.data?.novel_id || chapterRes.data?.bookId;
          if (resolvedBookId) {
            setBookId(resolvedBookId);
          }
        }
      } catch (err) {
        console.error('加载学习数据失败', err);
        setError('加载学习数据失败，请稍后再试');
      } finally {
        setLoading(false);
      }
    };

    if (chapterId) {
      fetchData();
    }
  }, [chapterId, bookIdFromUrl]);

  const groupedNotes = useMemo(() => {
    if (!Array.isArray(notes)) {
      return [];
    }

    let contentSentences = [];
    let translationSentences = [];

    if (chapter) {
      try {
        const parsedContent = JSON.parse(chapter.content || '[]');
        if (Array.isArray(parsedContent)) {
          contentSentences = parsedContent
            .map(item => item?.sentence_content || item?.sentence_content || item?.content || '')
            .filter(text => typeof text === 'string' && text.trim().length > 0);
        } else if (parsedContent?.sentences && Array.isArray(parsedContent.sentences)) {
          // 新格式：有 sentences 数组
          contentSentences = parsedContent.sentences
            .map(item => item?.sentence_content || item?.sentence_content || item?.content || '')
            .filter(text => typeof text === 'string' && text.trim().length > 0);
        } else if (parsedContent?.sentences && Array.isArray(parsedContent.sentences)) {
          contentSentences = parsedContent.sentences
            .map(item => item?.sentence_content || item?.sentence_content || item?.content || '')
            .filter(text => typeof text === 'string' && text.trim().length > 0);
        } else if (typeof parsedContent?.sentence_content === 'string') {
          contentSentences = [parsedContent.sentence_content];
        } else if (typeof parsedContent?.sentence_content === 'string') {
          contentSentences = [parsedContent.sentence_content];
        }
      } catch (error) {
        contentSentences = (chapter.content || '')
          .split(/\n+/)
          .filter(text => text.trim());
      }

      if (chapter.content_trans) {
        try {
          const parsedTrans = JSON.parse(chapter.content_trans || '[]');
          if (Array.isArray(parsedTrans)) {
            translationSentences = parsedTrans
              .map(item => item?.sentence_content || item?.sentence_content || item?.content || '')
              .filter(text => typeof text === 'string' && text.trim().length > 0);
          } else if (parsedTrans?.sentences && Array.isArray(parsedTrans.sentences)) {
            // 新格式：有 sentences 数组
            translationSentences = parsedTrans.sentences
              .map(item => item?.sentence_content || item?.sentence_content || item?.content || '')
              .filter(text => typeof text === 'string' && text.trim().length > 0);
          } else if (parsedTrans?.sentences && Array.isArray(parsedTrans.sentences)) {
            translationSentences = parsedTrans.sentences
              .map(item => item?.sentence_content || item?.sentence_content || item?.content || '')
              .filter(text => typeof text === 'string' && text.trim().length > 0);
          } else if (typeof parsedTrans?.sentence_content === 'string') {
            translationSentences = [parsedTrans.sentence_content];
          } else if (typeof parsedTrans?.sentence_content === 'string') {
            translationSentences = [parsedTrans.sentence_content];
          }
        } catch (error) {
          translationSentences = (chapter.content_trans || '')
            .split(/\n+/)
            .filter(text => text.trim());
        }
      }
    }

    if (!notes.length) {
      return [];
    }

    const chapterNumericId = chapter?.id ?? (chapterId ? Number(chapterId) : null);

    let aiNotesData = [];
    if (chapter?.ai_notes) {
      try {
        const parsed = typeof chapter.ai_notes === 'string'
          ? JSON.parse(chapter.ai_notes)
          : chapter.ai_notes;
        if (Array.isArray(parsed)) {
          aiNotesData = parsed;
        }
      } catch (error) {
        console.error('解析章节 AI 笔记失败:', error);
      }
    }

    const buildAiNoteParts = (sentenceId) => {
      if (!Array.isArray(aiNotesData) || !Number.isFinite(sentenceId)) {
        return null;
      }
      const aiNote = aiNotesData.find((item) => {
        const rawId = item?.sentence_id ?? item?.sentenceId;
        return Number(rawId) === sentenceId;
      });
      if (!aiNote) {
        return null;
      }
      const keyPhrases = Array.isArray(aiNote.key_phrases)
        ? aiNote.key_phrases
        : Array.isArray(aiNote.keyPhrases)
          ? aiNote.keyPhrases
          : [];
      const noteBody = keyPhrases
        .map((phrase) => {
          if (!phrase || typeof phrase !== 'object') {
            return '';
          }
          const blocks = [];
          const title = [phrase.phrase || '', phrase.meaning || '']
            .filter(Boolean)
            .join('：')
            .trim();
          if (title) {
            blocks.push(title);
          }
          if (phrase.usage_note) {
            blocks.push(`用法：${phrase.usage_note}`);
          }
          if (phrase.example_en) {
            blocks.push(`例句：${phrase.example_en}`);
          }
          if (phrase.example_cn) {
            blocks.push(phrase.example_cn);
          }
          return blocks.join('\n');
        })
        .filter(Boolean)
        .join('\n\n');
      return {
        phrases: keyPhrases,
        noteText: noteBody,
      };
    };

    const groups = new Map();

    notes.forEach((rawNote) => {
      const sentenceRaw = rawNote?.sentence_id ?? rawNote?.sentenceId;
      let numericSentenceId = null;
      if (typeof sentenceRaw === 'number' && Number.isFinite(sentenceRaw)) {
        numericSentenceId = sentenceRaw;
      } else if (typeof sentenceRaw === 'string' && sentenceRaw.trim().length > 0) {
        const parsed = Number(sentenceRaw);
        if (Number.isFinite(parsed)) {
          numericSentenceId = parsed;
        }
      }

      const lookupIndex =
        Number.isFinite(numericSentenceId) && numericSentenceId !== null
          ? numericSentenceId > 0
            ? numericSentenceId - 1
            : numericSentenceId
          : null;

      const chapterRaw = rawNote?.chapter_id ?? rawNote?.chapterId ?? chapterNumericId;
      const chapterRef = typeof chapterRaw === 'number' && Number.isFinite(chapterRaw)
        ? chapterRaw
        : (() => {
            const parsed = Number(chapterRaw);
            return Number.isFinite(parsed) ? parsed : null;
          })();

      const groupKey = `${chapterRef ?? 'chapter'}-${numericSentenceId ?? 'unknown'}`;

      const originalText =
        Number.isFinite(lookupIndex) && lookupIndex !== null && lookupIndex >= 0
          ? contentSentences[lookupIndex] || ''
          : '';
      const translatedText =
        Number.isFinite(lookupIndex) && lookupIndex !== null && lookupIndex >= 0
          ? translationSentences[lookupIndex] || ''
          : '';

      let group = groups.get(groupKey);
      if (!group) {
        const aiNoteInfo = numericSentenceId != null ? buildAiNoteParts(numericSentenceId) : null;
        group = {
          key: groupKey,
          chapterId: chapterRef ?? null,
          sentenceId: Number.isFinite(numericSentenceId) ? numericSentenceId : null,
          originalText,
          translatedText,
          notes: [],
          createdAt: null,
          isAiNote: rawNote?.note_content === '-1',
          aiPhrases: aiNoteInfo?.phrases ?? [],
          noteText: aiNoteInfo?.noteText ?? '',
        };
        groups.set(groupKey, group);
      }

      const noteId = rawNote?.id ?? rawNote?.favorite_id ?? rawNote?.favoriteId;
      const createdAt = rawNote?.created_at ?? rawNote?.createdAt ?? null;
      const normalizedNote = {
        ...rawNote,
        id: noteId ?? rawNote?.id,
        content: rawNote?.note_content ?? rawNote?.content ?? '',
        createdAt,
      };

      group.notes.push(normalizedNote);

      if (!group.createdAt && createdAt) {
        group.createdAt = createdAt;
      }
    });

    const groupedArray = Array.from(groups.values());

    groupedArray.forEach((group) => {
      group.notes.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
      if (!group.createdAt && group.notes[0]?.createdAt) {
        group.createdAt = group.notes[0].createdAt;
      }
      if (group.isAiNote && !group.noteText && group.notes[0]?.content) {
        group.noteText = group.notes[0].content;
      }
    });

    groupedArray.sort((a, b) => {
      const aIdx = Number.isFinite(a.sentenceId) ? a.sentenceId : Number.MAX_SAFE_INTEGER;
      const bIdx = Number.isFinite(b.sentenceId) ? b.sentenceId : Number.MAX_SAFE_INTEGER;
      if (aIdx !== bIdx) {
        return aIdx - bIdx;
      }
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });

    return groupedArray;
  }, [notes, chapter, chapterId]);

  const handleToggleAllWordDefinitions = () => {
    setShowAllWordDefinitions(prev => !prev);
  };

  const fetchSentenceDetail = async (wordItem) => {
    // 优先使用 wordItem 的 chapter_id，如果没有则使用 URL 参数中的 chapterId
    const targetChapterId = wordItem?.chapter_id || chapterId;
    
    if (!wordItem?.id || !targetChapterId) {
      return null;
    }

    try {
      const response = await api.get(`/chapters/${targetChapterId}`);
      const chapter = response.data;

      let contentSentences = [];
      let translationSentences = [];

      try {
        const contentData = JSON.parse(chapter.content);
        if (Array.isArray(contentData)) {
          contentSentences = contentData
            .map(item => item.sentence_content || item.sentence_content || item.content || '')
            .filter(text => text.trim());
        } else if (contentData?.sentences && Array.isArray(contentData.sentences)) {
          // 新格式：有 sentences 数组
          contentSentences = contentData.sentences
            .map(item => item.sentence_content || item.sentence_content || item.content || '')
            .filter(text => text.trim());
        } else if (contentData?.sentences && Array.isArray(contentData.sentences)) {
          contentSentences = contentData.sentences
            .map(item => item.sentence_content || item.sentence_content || item.content || '')
            .filter(text => text.trim());
        } else {
          const text = contentData?.sentence_content || contentData?.sentence_content || contentData?.content || '';
          if (text) {
            contentSentences = [text];
          }
        }
      } catch (error) {
        contentSentences = (chapter.content || '')
          .split(/\n+/)
          .filter(sentence => sentence.trim());
      }

      if (chapter.content_trans) {
        try {
          const transData = JSON.parse(chapter.content_trans);
          if (Array.isArray(transData)) {
            translationSentences = transData
              .map(item => item.sentence_content || item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else if (transData?.sentences && Array.isArray(transData.sentences)) {
            // 新格式：有 sentences 数组
            translationSentences = transData.sentences
              .map(item => item.sentence_content || item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else if (transData?.sentences && Array.isArray(transData.sentences)) {
            translationSentences = transData.sentences
              .map(item => item.sentence_content || item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else {
            const text = transData?.sentence_content || transData?.sentence_content || transData?.content || '';
            if (text) {
              translationSentences = [text];
            }
          }
        } catch (error) {
          translationSentences = chapter.content_trans
            .split(/\n+/)
            .filter(sentence => sentence.trim());
        }
      }

      const sentenceIndex = wordItem.sentence_id;
      const originalText = contentSentences[sentenceIndex] || '';
      const translatedText = translationSentences[sentenceIndex] || '';

      let chapterTitle = chapter.title;
      try {
        const parsedContent = JSON.parse(chapter.content);
        chapterTitle = parsedContent.chapter_title || chapter.title;
      } catch (error) {
        // ignore json parse error
      }

      return {
        chapterTitle,
        originalText,
        translatedText,
      };
    } catch (error) {
      console.error('获取句子详情失败:', error);
      return null;
    }
  };

  const handleToggleAllWordSentences = async () => {
    const shouldShow = !showAllWordSentences;
    setShowAllWordSentences(shouldShow);

    if (shouldShow && Array.isArray(words) && words.length > 0) {
      const newDetails = {};
      for (const wordItem of words) {
        const wordId = wordItem?.id;
        
        if (!wordId || expandedSentences[wordId] || newDetails[wordId]) {
          continue;
        }
        
        const detail = await fetchSentenceDetail(wordItem);
        
        if (detail) {
          newDetails[wordId] = detail;
        }
      }

      if (Object.keys(newDetails).length > 0) {
        setExpandedSentences(prev => ({ ...prev, ...newDetails }));
      }
    }
  };

  const handleToggleSentenceChinese = () => {
    setShowSentenceChinese(prev => !prev);
  };

  const handleToggleNoteChinese = () => {
    setShowNoteChinese(prev => !prev);
  };

  const handleToggleNoteEnglish = () => {
    setShowNoteEnglish(prev => !prev);
  };

  const handleNoteFavoriteToggle = async (note) => {
    const noteId = note?.id ?? note?.favorite_id ?? note?.favoriteId;
    if (!noteId) {
      return;
    }

    setNoteFavoriteLoading((prev) => ({ ...prev, [noteId]: true }));

    try {
      await api.delete(`/notes/${noteId}`);
      setNotes((prev) => prev.filter((item) => (item.id ?? item.favorite_id ?? item.favoriteId) !== noteId));
    } catch (error) {
      console.error('取消收藏笔记失败:', error);
      if (error?.response?.status === 401) {
        alert('请先登录后再操作');
      } else {
        alert('取消收藏失败，请稍后重试');
      }
    } finally {
      setNoteFavoriteLoading((prev) => {
        const next = { ...prev };
        delete next[noteId];
        return next;
      });
    }
  };

  const handleSentenceFavoriteToggle = async (sentence) => {
    const sentenceId = sentence?.id ?? sentence?.favorite_id ?? sentence?.favoriteId;
    if (!sentenceId) {
      return;
    }

    setSentenceFavoriteLoading((prev) => ({ ...prev, [sentenceId]: true }));

    try {
      await api.delete(withApiPrefix(`favorite-sentences/${sentenceId}`));
      setSentences((prev) => prev.filter((item) => (item.id ?? item.favorite_id ?? item.favoriteId) !== sentenceId));
    } catch (error) {
      console.error('取消收藏句子失败:', error);
      if (error?.response?.status === 401) {
        alert('请先登录后再操作');
      } else {
        alert('取消收藏失败，请稍后重试');
      }
    } finally {
      setSentenceFavoriteLoading((prev) => {
        const next = { ...prev };
        delete next[sentenceId];
        return next;
      });
    }
  };

  const renderSettingsContent = () => {
    if (activeTab === 'sentences') {
      return (
        <div className={styles.settingsRow}>
          <div className={styles.settingsText}>
            <span className={styles.settingTitle}>显示中文</span>
            {/* <span className={styles.settingDescription}>关闭后仅显示英文句子</span> */}
          </div>
          <button
            type="button"
            className={`${styles.toggleButton} ${showSentenceChinese ? styles.toggleButtonActive : ''}`}
            onClick={handleToggleSentenceChinese}
            role="switch"
            aria-checked={showSentenceChinese}
          >
            <span className={styles.toggleThumb} />
          </button>
        </div>
      );
    }

    if (activeTab === 'notes') {
      return (
        <>
          <div className={styles.settingsRow}>
            <div className={styles.settingsText}>
              <span className={styles.settingTitle}>显示中文</span>
            </div>
            <button
              type="button"
              className={`${styles.toggleButton} ${showNoteChinese ? styles.toggleButtonActive : ''}`}
              onClick={handleToggleNoteChinese}
              role="switch"
              aria-checked={showNoteChinese}
            >
              <span className={styles.toggleThumb} />
            </button>
          </div>
          <div className={styles.settingsRow}>
            <div className={styles.settingsText}>
              <span className={styles.settingTitle}>显示英文</span>
            </div>
            <button
              type="button"
              className={`${styles.toggleButton} ${showNoteEnglish ? styles.toggleButtonActive : ''}`}
              onClick={handleToggleNoteEnglish}
              role="switch"
              aria-checked={showNoteEnglish}
            >
              <span className={styles.toggleThumb} />
            </button>
          </div>
        </>
      );
    }

    // 默认展示单词设置
    return (
      <>
        <div className={styles.settingsRow}>
          <div className={styles.settingsText}>
            <span className={styles.settingTitle}>单词释义</span>
            {/* <span className={styles.settingDescription}>开启后显示所有收藏单词的词典释义</span> */}
          </div>
          <button
            type="button"
            className={`${styles.toggleButton} ${showAllWordDefinitions ? styles.toggleButtonActive : ''}`}
            onClick={handleToggleAllWordDefinitions}
            role="switch"
            aria-checked={showAllWordDefinitions}
          >
            <span className={styles.toggleThumb} />
          </button>
        </div>
        <div className={styles.settingsRow}>
          <div className={styles.settingsText}>
            <span className={styles.settingTitle}>所在句子</span>
            {/* <span className={styles.settingDescription}>开启后展开收藏单词对应的原文与译文句子</span> */}
          </div>
          <button
            type="button"
            className={`${styles.toggleButton} ${showAllWordSentences ? styles.toggleButtonActive : ''}`}
            onClick={handleToggleAllWordSentences}
            role="switch"
            aria-checked={showAllWordSentences}
          >
            <span className={styles.toggleThumb} />
          </button>
        </div>
      </>
    );
  };

  const handleFavoriteToggle = async (wordItem) => {
    if (!wordItem?.id) {
      return;
    }

    const wordId = wordItem.id;
    setFavoriteLoadingMap(prev => ({ ...prev, [wordId]: true }));

    try {
      await api.delete(`/favorite-words/${wordId}`);
      setWords(prev => prev.filter(item => item.id !== wordId));
      setExpandedSentences(prev => {
        if (!prev[wordId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[wordId];
        return next;
      });
    } catch (error) {
      console.error('取消收藏单词失败:', error);
      if (error?.response?.status === 401) {
        alert('请先登录后再操作');
      } else {
        alert('取消收藏失败，请稍后重试');
      }
    } finally {
      setFavoriteLoadingMap(prev => {
        const next = { ...prev };
        delete next[wordId];
        return next;
      });
    }
  };

  const handleGoToChapter = (targetChapterId, targetSentenceId) => {
    // 如果没有指定章节ID，使用当前章节ID
    const finalChapterId = targetChapterId || chapterId;
    
    if (!bookId || !finalChapterId) {
      return;
    }

    const highlightParam =
      targetSentenceId !== undefined && targetSentenceId !== null
        ? `?highlight=${targetSentenceId}`
        : '';

    navigate(`/read?bookid=${bookId}&chapterid=${finalChapterId}${highlightParam}`);
  };

  const handleWordPronunciation = (word, type = 0) => {
    const cleanWord = word?.trim();
    if (!cleanWord) {
      return;
    }
    try {
      const audio = new Audio(`http://dict.youdao.com/dictvoice?type=${type}&audio=${encodeURIComponent(cleanWord)}`);
      audio.play();
    } catch (error) {
      console.error('播放单词发音失败:', error);
    }
  };

  const handleOpenWordModal = async (wordItem) => {
    if (!wordItem?.word) {
      return;
    }

    const targetWord = wordItem.word.trim();
    const wordData = wordItem.word_jsondata || {};
    const youdaoDefinition =
      wordData.youdao_definition ||
      wordData.youdaoDefinition ||
      wordData.definition ||
      null;
    const collinsDefinition = wordData.collins_definition || wordData.collinsDefinition || null;

    setSelectedWord({
      word: targetWord,
      success: true,
      youdao_definition: youdaoDefinition,
      collins_definition: collinsDefinition,
      phonetic: wordData.phonetic,
    });

    setWordModalDictResults({
      youdao: youdaoDefinition
        ? { success: true, data: { definition: youdaoDefinition } }
        : null,
      collins: collinsDefinition
        ? { success: true, data: { definition: collinsDefinition } }
        : null,
    });

    const defaultDict = youdaoDefinition ? 'youdao' : collinsDefinition ? 'collins' : 'youdao';
    setWordModalActiveDict(defaultDict);
    setWordModalLoading({ youdao: false, collins: false });
    setWordModalOpen(true);
  };

  const handleCloseWordModal = () => {
    setWordModalOpen(false);
    setSelectedWord(null);
    setWordModalDictResults({ youdao: null, collins: null });
    setWordModalLoading({ youdao: false, collins: false });
    setWordModalActiveDict('youdao');
  };

  const handleWordModalDictTabClick = (dictKey) => {
    if (!dictKey) return;
    const result = wordModalDictResults?.[dictKey];
    if (result && result.success !== false) {
      setWordModalActiveDict(dictKey);
    }
  };

  const buildReturnLocation = () => ({
    pathname: location.pathname,
    search: location.search || '',
    hash: location.hash || '',
  });
  const buildReturnUrl = () => `${location.pathname}${location.search || ''}${location.hash || ''}`;

  const handleStartLearning = () => {
    console.log('[ChapterLearn] handleStartLearning called, bookId:', bookId, 'chapterId:', chapterId);
    if (!bookId || !chapterId) {
      console.log('[ChapterLearn] handleStartLearning ABORTED: missing bookId or chapterId');
      return;
    }
    const targetUrl = `/learn/word?chapterid=${chapterId}&bookid=${bookId}`;
    console.log('[ChapterLearn] Navigating to:', targetUrl);
    window.sessionStorage.setItem('wordStudyForcePlay', 'true');
    const backTo = buildReturnLocation();
    navigate(targetUrl, {
      state: {
        fromChapterId: Number(chapterId),
        from: 'chapterLearn',
        backTo,
      },
    });
  };

  const handleStartSentenceLearning = () => {
    console.log('[ChapterLearn] handleStartSentenceLearning called, bookId:', bookId, 'chapterId:', chapterId);
    if (!bookId || !chapterId) {
      console.log('[ChapterLearn] handleStartSentenceLearning ABORTED: missing bookId or chapterId');
      return;
    }
    const targetUrl = `/learn/sentences?chapterid=${chapterId}&bookid=${bookId}`;
    console.log('[ChapterLearn] Navigating to:', targetUrl);
    window.sessionStorage.setItem('sentenceStudyForcePlay', 'true');
    const returnPath = buildReturnUrl();
    navigate(targetUrl, {
      state: {
        from: 'chapterLearn',
        returnPath,
        bookId,
      },
    });
  };

  const handleStartNoteLearning = () => {
    console.log('[ChapterLearn] handleStartNoteLearning called, bookId:', bookId, 'chapterId:', chapterId);
    if (!bookId || !chapterId) {
      console.log('[ChapterLearn] handleStartNoteLearning ABORTED: missing bookId or chapterId');
      return;
    }
    const targetUrl = `/learn/notes?chapterid=${chapterId}&bookid=${bookId}`;
    console.log('[ChapterLearn] Navigating to:', targetUrl);
    window.sessionStorage.setItem('noteStudyForcePlay', 'true');
    const returnPath = buildReturnUrl();
    navigate(targetUrl, {
      state: {
        from: 'chapterLearn',
        returnPath,
        focusChapterId: Number(chapterId),
      },
    });
  };
  const tabItems = useMemo(
    () => [
      { key: 'words', label: '单词' },
      { key: 'sentences', label: '句子' },
      { key: 'notes', label: '笔记' },
    ],
    []
  );
  const tabKeys = useMemo(() => tabItems.map((item) => item.key), [tabItems]);
  const defaultTabKey = tabItems[0]?.key ?? 'words';
  const activeTab = tabParam && tabKeys.includes(tabParam) ? tabParam : defaultTabKey;

  useEffect(() => {
    if (!chapterId) {
      return;
    }
    if (!tabParam || !tabKeys.includes(tabParam)) {
      navigate(`/chapterlearn?chapterid=${chapterId}&bookid=${bookId}&tab=${defaultTabKey}`, { replace: true });
    }
  }, [chapterId, tabParam, tabKeys, defaultTabKey, navigate]);
  return (
    <div className={styles.wrapper}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(`/read?bookid=${bookId}&chapterid=${chapterId}`)}
            aria-label="返回章节阅读"
          >
            <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
          </button>
        </div>
        <div className={styles.tabNav}>
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tabButton} ${activeTab === tab.key ? styles.tabButtonActive : ''}`}
              onClick={() => {
                if (tab.key !== activeTab && chapterId) {
                  navigate(`/chapterlearn?chapterid=${chapterId}&bookid=${bookId}&tab=${tab.key}`);
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.topBarRight}>
          <button
            type="button"
            className={styles.topSettingsBtn}
            onClick={() => setShowSettings((prev) => !prev)}
            aria-label="学习设置"
            aria-haspopup="true"
            aria-expanded={showSettings}
          >
            <SettingsIcon size={18} />
          </button>
          {showSettings && (
            <div
              className={styles.topSettingsPanel}
              role="dialog"
              aria-label="学习设置"
            >
              <div className={styles.settingsContent}>{renderSettingsContent()}</div>
            </div>
          )}
        </div>
      </div>

 

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.tabContent}>
        {activeTab === 'words' && (
          <>
            {Array.isArray(words) && words.length > 0 && (
              <div className={styles.learningAction}>
                <span className={styles.collectCount}>本章共收藏 {words.length} 个单词</span>
                <button
                  type="button"
                  className={styles.startLearningBtn}
                  onClick={handleStartLearning}
                >
                  去学习 <span className="material-icons-round" style={{ fontSize: '18px', verticalAlign: 'middle' }}>arrow_forward</span>
                </button>
              </div>
            )}
            <WordsSection
              wordsLoading={loading}
              chapterWords={words}
              showAllWordDefinitions={showAllWordDefinitions}
              onToggleAllWordDefinitions={handleToggleAllWordDefinitions}
              showAllWordSentences={showAllWordSentences}
              onToggleAllWordSentences={handleToggleAllWordSentences}
              expandedSentences={expandedSentences}
              onGoToChapter={handleGoToChapter}
              favoriteLoadingMap={favoriteLoadingMap}
              onFavoriteToggle={handleFavoriteToggle}
              onStartLearning={handleStartLearning}
              onShowWordDetail={handleOpenWordModal}
            />
          </>
        )}

        {activeTab === 'sentences' && (
          <>
            {loading ? (
              <div className={styles.loading}>
                <LoadingSpinner size="small" text="加载中..." />
              </div>
            ) : sentences && sentences.length > 0 ? (
              <>
                <div className={styles.learningAction}>
                  <span className={styles.collectCount}>本章共收藏 {sentences.length} 个句子</span>
                  <button
                    type="button"
                    className={styles.startLearningBtn}
                    onClick={handleStartSentenceLearning}
                  >
                    去学习 <span className="material-icons-round" style={{ fontSize: '18px', verticalAlign: 'middle' }}>arrow_forward</span>
                  </button>
                </div>
                <div className={styles.sentenceList}>
                  {sentences.map((sentence) => {
                    const sentenceId = sentence.id ?? sentence.favorite_id ?? sentence.favoriteId ?? sentence.sentence_id;
                    return (
                      <SentenceCard
                        key={sentenceId}
                        sentence={sentence}
                        showChinese={showSentenceChinese}
                        favoriteLoading={!!sentenceFavoriteLoading[sentenceId]}
                        onFavoriteToggle={handleSentenceFavoriteToggle}
                        playingClip={playingClip}
                        onPlayClip={playSentenceClip}
                        onStopClip={(lang) => stopClip(lang, true)}
                      />
                    );
                  })}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <h1 className={styles.emptyTitle}>暂无收藏句子</h1>
                <p className={styles.emptyDescription}>
                  在阅读时，
                  <span className={styles.emptyHighlight}>长按或点击句子旁的收藏按钮</span>
                  即可将喜欢的句子收藏到这里，方便复习巩固。
                </p>
                <button
                  type="button"
                  className={styles.emptyActionBtn}
                  onClick={() => navigate(`/read?bookid=${bookId}&chapterid=${chapterId}`)}
                >
                  去阅读并收藏
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>arrow_forward</span>
                </button>
                <div className={styles.emptyHint}>
                  <span className="material-icons-outlined" style={{ fontSize: '16px' }}>format_quote</span>
                  <span>阅读时点击句子的收藏按钮即可收藏</span>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'notes' && (
          <>
            {loading ? (
              <div className={styles.loading}>
                <LoadingSpinner size="small" text="加载中..." />
              </div>
            ) : Array.isArray(groupedNotes) && groupedNotes.length > 0 ? (
              <>
                <div className={styles.learningAction}>
                  <span className={styles.collectCount}>本章共保存 {groupedNotes.length} 条笔记</span>
                  <button
                    type="button"
                    className={styles.startLearningBtn}
                    onClick={handleStartNoteLearning}
                  >
                    去学习 <span className="material-icons-round" style={{ fontSize: '18px', verticalAlign: 'middle' }}>arrow_forward</span>
                  </button>
                </div>
                <div className={styles.noteList}>
                  {groupedNotes.map((group) => (
                    <NoteGroupCard
                      key={group.key}
                      group={group}
                      showChinese={showNoteChinese}
                      showEnglish={showNoteEnglish}
                      favoriteLoadingMap={noteFavoriteLoading}
                      onFavoriteToggle={handleNoteFavoriteToggle}
                      playingClip={playingClip}
                      onPlayClip={playSentenceClip}
                      onStopClip={(lang) => stopClip(lang, true)}
                      chapterId={Number(chapterId)}
                      sentenceTimings={sentenceTimings}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <h1 className={styles.emptyTitle}>暂无保存笔记</h1>
                <p className={styles.emptyDescription}>
                  在阅读时，
                  <span className={styles.emptyHighlight}>点击句子旁的笔记按钮</span>
                  即可为句子添加笔记，记录你的理解和思考。
                </p>
                <button
                  type="button"
                  className={styles.emptyActionBtn}
                  onClick={() => navigate(`/read?bookid=${bookId}&chapterid=${chapterId}`)}
                >
                  去阅读并记笔记
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>arrow_forward</span>
                </button>
                <div className={styles.emptyHint}>
                  <span className="material-icons-outlined" style={{ fontSize: '16px' }}>edit_note</span>
                  <span>阅读时点击笔记按钮即可记录</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <WordModal
        isOpen={wordModalOpen}
        wordLookup={selectedWord}
        activeDict={wordModalActiveDict}
        dictLoading={wordModalLoading}
        dictResults={wordModalDictResults}
        favoriteLoading={false}
        isFavorited={true}
        onPlayPronunciation={(voiceType) => selectedWord?.word && handleWordPronunciation(selectedWord.word, voiceType)}
        onFavoriteWord={() => {}}
        onDictTabClick={handleWordModalDictTabClick}
        onDefinitionClick={() => {}}
        onClose={handleCloseWordModal}
      />
    </div>
  );
};

export default ChapterLearn;
